import { BuyerNotificationSettingRecipientRoleVO } from "../types/model/valueObject/notificationSettings/BuyerNotificationSettingRecipientRoleVO";
import { SupplierNotificationSettingRecipientRoleVO } from "../types/model/valueObject/notificationSettings/SupplierNotificationSettingRecipientRoleVO";


export interface NotificationRoleDefinitionType {
  value: string;
  label: string;
  // Optional one-line descriptor shown under the role name in the redesigned
  // matrix / cards (e.g. "Primary account holder"). Purely descriptive.
  sub?: string;
}

export interface NotificationChannelDefinitionType {
  value: string;
  label: string;
}

export interface NotificationCategoryDefinitionType {
  value: string;
  label: string;
  tooltip: string;
}

export interface NotificationUniversalSettingDefinitionType {
  propertyName: string;
  label: string;
  tooltip: string;
  //type: 'boolean' | 'string' | 'number';  cmm: assume all boolean for now, can add more types if needed
}

export interface NotificationUniversalSettingType {
  [key: string]: boolean;
}

export interface SerializedNotificationType {
  channel: string;
  category: string;
  role: string;
}

export interface NotificationRowType {
  [key: string]: boolean;
}

export interface NotificationType {
  [key: string]: NotificationRowType;
}

export interface NotificationTypeChannels {
  [key: string]: NotificationType;
}

export interface NotificationChannelDefinitionsType {
  [key: string]: NotificationChannelDefinitionType;
}

export const NotificationChannelsDefinitions: NotificationChannelDefinitionsType = Object.freeze({
  EMAIL: {
    value: 'EMAIL',
    label: 'Email',
  },
  SMS: {
    value: 'SMS',
    label: 'Text',
  },
});

export const buildDefaultNotificationRowType = (roles: NotificationRoleDefinitionType[]): NotificationRowType => {
  const rowType: NotificationRowType = {};
  roles.forEach (role => {
    rowType[role.value] = false;
  });
  return rowType;
}

export const deserializeRecipientRoles = (
  recipientRoles: BuyerNotificationSettingRecipientRoleVO[] | SupplierNotificationSettingRecipientRoleVO[],
  channelDefs: NotificationChannelDefinitionType[],
  categoryDefs: NotificationCategoryDefinitionType[],
  roleDefs: NotificationRoleDefinitionType[]
): NotificationTypeChannels => {
  const deserialized: NotificationTypeChannels = {};

  channelDefs.forEach(channel => {
    const channelSettings: NotificationType = {};
    categoryDefs.forEach(category => {
      channelSettings[category.value] = buildDefaultNotificationRowType(roleDefs);
    });
    deserialized[channel.value] = channelSettings;
  });

  recipientRoles.forEach(recipientRole => {
    deserialized[recipientRole.channel][recipientRole.category][recipientRole.role] = true;
  });

  return deserialized;
}

export interface NotificationSettingsData {
  recipientRoles?: BuyerNotificationSettingRecipientRoleVO[] | SupplierNotificationSettingRecipientRoleVO[];
}

/**
 * Builds the matrix enrollment state from a notification-settings GET response.
 *
 * Returns `undefined` while the request has not resolved yet (`data` is
 * undefined). This is deliberate: deriving from an unresolved response would
 * treat "not loaded yet" as "loaded but empty" (via `|| []`) and paint the
 * all-default state for a frame before the saved state arrives. Callers should
 * leave their state undefined (and render nothing / a loader) until this returns
 * a value. A loaded-but-empty response (`{ recipientRoles: [] }`) still returns a
 * real, all-false settings object.
 */
export const buildNotificationSettingsFromData = (
  data: NotificationSettingsData | undefined | null,
  channelDefs: NotificationChannelDefinitionType[],
  categoryDefs: NotificationCategoryDefinitionType[],
  roleDefs: NotificationRoleDefinitionType[]
): NotificationTypeChannels | undefined => {
  if (!data) return undefined;
  return deserializeRecipientRoles(data.recipientRoles || [], channelDefs, categoryDefs, roleDefs);
}

export const serializeRecipientRoles = (
  deserializedSettings: NotificationTypeChannels
): SerializedNotificationType[] => {
  const recipientRoles: SerializedNotificationType[] = [];
  for (const channel in deserializedSettings) {
    for (const category in deserializedSettings[channel]) {
      for (const role in deserializedSettings[channel][category]) {
        if (deserializedSettings[channel][category][role]) {
          recipientRoles.push({
            channel,
            category,
            role
          });
        }
      }
    }
  }
  return recipientRoles;
}

/* ============================================================
   Account Owner fallback rule
   ------------------------------------------------------------
   For any (channel, category) pair at least one recipient must be
   enrolled. The Account Owner — by convention the FIRST role in the
   roles array — is the guaranteed recipient: when no other role is
   enrolled for that channel+category the owner is forced ON and locked.
   The locked/forced-on state is DERIVED at render time, never stored.
   `applyOwnerFallback` materialises it into the payload at save time so
   the persisted data always satisfies the "at least one recipient" rule.
   ============================================================ */

// True when any role OTHER than the owner (roles[0]) is enrolled in this row.
// `row` is settings[channel][category] — a map of roleValue -> boolean.
export const othersEnrolled = (
  roles: NotificationRoleDefinitionType[],
  row: NotificationRowType | undefined
): boolean => {
  if (roles.length === 0) return false;
  const ownerValue = roles[0].value;
  return roles.some(role => role.value !== ownerValue && !!row?.[role.value]);
};

// The owner is forced ON + locked when no other role covers this row.
export const isOwnerLocked = (
  roles: NotificationRoleDefinitionType[],
  row: NotificationRowType | undefined
): boolean => {
  if (roles.length === 0) return false;
  return !othersEnrolled(roles, row);
};

// Effective (displayed) value: owner reads as ON whenever it is locked,
// otherwise every role reads its stored boolean.
export const effectiveOn = (
  roles: NotificationRoleDefinitionType[],
  row: NotificationRowType | undefined,
  roleValue: string
): boolean => {
  if (roles.length > 0 && roleValue === roles[0].value && isOwnerLocked(roles, row)) {
    return true;
  }
  return !!row?.[roleValue];
};

// Returns a copy of the settings with the owner forced ON in every
// (channel, category) where the fallback rule locks it. Use this before
// serialising so the persisted payload matches what the user sees.
export const applyOwnerFallback = (
  settings: NotificationTypeChannels,
  roles: NotificationRoleDefinitionType[]
): NotificationTypeChannels => {
  if (roles.length === 0) return settings;
  const ownerValue = roles[0].value;
  const next: NotificationTypeChannels = {};
  for (const channel in settings) {
    next[channel] = {};
    for (const category in settings[channel]) {
      const row = { ...settings[channel][category] };
      if (isOwnerLocked(roles, row)) {
        row[ownerValue] = true;
      }
      next[channel][category] = row;
    }
  }
  return next;
};